import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { 
  FileText, ArrowRight, Sparkles, Play, Menu, Globe, BookOpen, Zap, Star
} from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { VideoModal } from "@/components/landing/VideoModal";
import { HeroSearchDemo } from "@/components/landing/HeroSearchDemo";
import { TrustBlock } from "@/components/landing/TrustBlock";
import { UnifiedFeatures } from "@/components/landing/UnifiedFeatures";
import { EasySyncSection } from "@/components/landing/EasySyncSection";
import { B2BSection } from "@/components/landing/B2BSection";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { JsonLd } from "@/components/seo/JsonLd";
import { SEO } from "@/components/seo/SEO";

import easyfinderLogo from "@/assets/easyfinder-logo-google.png";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const Landing = () => {
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, lang, setLanguage } = useTranslation();
  
  const heroRef = useRef<HTMLDivElement>(null);
  
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 overflow-x-hidden texture-grain">
      <SEO 
        title="Gestion Intelligente de Documents avec IA"
        description="Recherchez vos documents en langage naturel avec l'IA. OCR automatique, classification intelligente, partage sécurisé. Essai gratuit."
        canonical="/"
      />
      <JsonLd />
      {/* Ambient background effects - Premium indigo glow */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-primary/8 rounded-full blur-[120px] animate-pulse-slow" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-accent/5 rounded-full blur-[100px] animate-pulse-slow" style={{ animationDelay: '2s' }} />
      </div>

      {/* Header - Glass */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-2xl bg-background/60 border-b border-white/10 shadow-lg shadow-black/5">
        <div className="container mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={easyfinderLogo} alt="EasyFinder" className="w-9 h-9 rounded-xl shadow-lg shadow-primary/25" />
            <span className="text-lg font-semibold tracking-tight">EasyFinder</span>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-300">
              {t.landing.nav.features}
            </a>
            <a href="#how-it-works" className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-300">
              {t.landing.nav.howItWorks}
            </a>
            <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-300">
              {t.landing.nav.pricing}
            </Link>
            <Link to="/blog" className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-300">
              Blog
            </Link>
          </nav>
          
          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9">
                  <Globe className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setLanguage('fr')} className={lang === 'fr' ? 'bg-muted' : ''}>
                  🇫🇷 Français
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setLanguage('en')} className={lang === 'en' ? 'bg-muted' : ''}>
                  🇬🇧 English
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Link to="/auth">
              <Button variant="ghost" size="sm" className="text-sm backdrop-blur-sm">
                {t.landing.nav.signIn}
              </Button>
            </Link>
            <Link to="/auth?mode=signup">
              <Button size="sm" variant="glow" className="text-sm">
                {t.landing.nav.getStarted}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className="h-10 w-10">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] sm:w-[320px] p-0">
              <div className="flex flex-col h-full">
                {/* Mobile Menu Header */}
                <div className="flex items-center justify-between p-4 border-b">
                  <div className="flex items-center gap-2">
                    <img src={easyfinderLogo} alt="EasyFinder" className="w-8 h-8 rounded-lg" />
                    <span className="font-semibold">EasyFinder</span>
                  </div>
                </div>
                
                {/* Mobile Menu Links */}
                <nav className="flex flex-col p-4 gap-2">
                  <a 
                    href="#features" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors text-foreground font-medium"
                  >
                    <Sparkles className="w-5 h-5 text-primary" />
                    {t.landing.nav.features}
                  </a>
                  <a 
                    href="#how-it-works" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors text-foreground font-medium"
                  >
                    <Zap className="w-5 h-5 text-primary" />
                    {t.landing.nav.howItWorks}
                  </a>
                  <Link 
                    to="/pricing" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors text-foreground font-medium"
                  >
                    <FileText className="w-5 h-5 text-primary" />
                    {t.landing.nav.pricing}
                  </Link>
                  <Link 
                    to="/blog" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted transition-colors text-foreground font-medium"
                  >
                    <BookOpen className="w-5 h-5 text-primary" />
                    Blog
                  </Link>
                  
                  {/* Language selector in mobile menu */}
                  <div className="flex items-center gap-2 px-4 py-3">
                    <Globe className="w-5 h-5 text-primary" />
                    <Button 
                      variant={lang === 'fr' ? 'secondary' : 'ghost'} 
                      size="sm" 
                      onClick={() => setLanguage('fr')}
                    >
                      FR
                    </Button>
                    <Button 
                      variant={lang === 'en' ? 'secondary' : 'ghost'} 
                      size="sm" 
                      onClick={() => setLanguage('en')}
                    >
                      EN
                    </Button>
                  </div>
                </nav>
                
                {/* Mobile Menu CTA */}
                <div className="mt-auto p-4 border-t space-y-3">
                  <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full">
                      {t.landing.nav.signIn}
                    </Button>
                  </Link>
                  <Link to="/auth?mode=signup" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="glow" className="w-full">
                      {t.landing.nav.startFree}
                    </Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Hero */}
      <section ref={heroRef} className="relative pt-28 pb-12 md:pt-36 md:pb-20 px-6">
        <motion.div 
          className="container mx-auto max-w-4xl text-center relative z-10"
          style={{ y: heroY }}
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          {/* Badge - Glass */}
          <motion.div
            variants={fadeInUp}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full backdrop-blur-xl bg-white/60 border border-white/20 shadow-lg shadow-black/5 text-sm mb-8"
          >
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-foreground/80 font-medium">{t.landing.hero.badge}</span>
          </motion.div>

          {/* Headline - Promise H1 */}
          <motion.h1 
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 leading-[1.1] font-display"
            variants={fadeInUp}
          >
            Votre assistant{" "}
            <span className="text-gradient">administratif</span>
            <br />
            intelligent.
          </motion.h1>
          
          {/* Subheadline - Proactive value prop */}
          <motion.p 
            className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed px-2"
            variants={fadeInUp}
          >
            EasyFinder surveille vos documents, vous alerte avant les échéances, et vous libère de la charge mentale administrative.
          </motion.p>

          {/* CTAs - Glass buttons */}
          <motion.div 
            className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-6 px-4"
            variants={fadeInUp}
          >
            <Link to="/auth?mode=signup" className="w-full sm:w-auto">
              <Button size="lg" variant="glow" className="w-full sm:w-auto sm:min-w-[220px] h-12 sm:h-13 text-base font-semibold group">
                Commencer gratuitement
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="glass" 
              className="w-full sm:w-auto sm:min-w-[220px] h-12 sm:h-13 text-base font-medium group"
              onClick={() => setVideoModalOpen(true)}
            >
              <Play className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform duration-300" />
              Voir la démo
            </Button>
          </motion.div>

          {/* Reassurance line */}
          <motion.p 
            className="text-sm text-muted-foreground mb-12 sm:mb-16"
            variants={fadeInUp}
          >
            Gratuit pour commencer · Sans carte bancaire · Vos données restent privées
          </motion.p>

          {/* Social Proof - Glass card */}
          <motion.div 
            className="inline-flex flex-wrap items-center justify-center gap-6 px-6 py-4 rounded-2xl backdrop-blur-xl bg-white/40 border border-white/20 shadow-xl shadow-black/5 text-sm text-muted-foreground"
            variants={fadeInUp}
          >
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                {["M", "S", "L", "A", "P"].map((letter, i) => (
                  <div 
                    key={i} 
                    className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 border-2 border-white flex items-center justify-center text-xs font-semibold text-primary shadow-sm"
                  >
                    {letter}
                  </div>
                ))}
              </div>
              <span className="font-medium">{t.landing.hero.users}</span>
            </div>
            <div className="w-px h-6 bg-border/50 hidden sm:block" />
            <div className="flex items-center gap-1.5">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400 drop-shadow-sm" />
              ))}
              <span className="ml-1 font-medium">{t.landing.hero.reviews}</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Hero Visual - Interactive Search Demo */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="container mx-auto max-w-3xl mt-12 md:mt-20 px-4 relative z-10"
        >
          <HeroSearchDemo />
        </motion.div>
      </section>

      {/* Trust Block - Stats & Integrations */}
      <TrustBlock />

      {/* Unified Features - Problem-oriented */}
      <UnifiedFeatures />

      {/* EasySync Section - 50+ organismes officiels */}
      <EasySyncSection />

      {/* B2B Section - Subcontractors */}
      <B2BSection />

      {/* Final CTA - Compact */}
      <section className="relative py-16 md:py-20 px-6 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary/80" />
        <div className="absolute inset-0 backdrop-blur-3xl bg-white/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-white/10 rounded-full blur-[80px]" />
        
        <div className="container mx-auto max-w-2xl text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-5"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-white leading-tight">
              Libérez-vous de la charge mentale.
            </h2>
            
            <p className="text-white/70 text-base md:text-lg max-w-md mx-auto">
              Ne ratez plus jamais une échéance importante.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <Link to="/auth?mode=signup">
                <Button 
                  size="lg" 
                  className="h-12 px-8 text-base font-semibold bg-white text-primary hover:bg-white/90 shadow-xl group"
                >
                  Commencer gratuitement
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button 
                  variant="ghost" 
                  size="lg"
                  className="h-12 px-6 text-white/80 hover:text-white hover:bg-white/10"
                >
                  Voir les tarifs
                </Button>
              </Link>
            </div>

            <p className="text-white/50 text-xs">
              Gratuit · Sans carte bancaire
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-8 px-6 backdrop-blur-xl bg-white/50 border-t border-white/20">
        <div className="container mx-auto max-w-5xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg shadow-primary/20">
                <FileText className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-bold">EasyFinder</span>
            </div>
            <nav className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
              <Link to="/blog" className="hover:text-foreground transition-colors duration-300">Blog</Link>
              <Link to="/security-faq" className="hover:text-foreground transition-colors duration-300">{t.landing.footer.securityFaq}</Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors duration-300 font-medium">{t.landing.footer.privacy}</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors duration-300">{t.landing.footer.terms}</Link>
              <Link to="/legal" className="hover:text-foreground transition-colors duration-300">{t.landing.footer.legal}</Link>
            </nav>
          </div>
        </div>
      </footer>

      <VideoModal open={videoModalOpen} onOpenChange={setVideoModalOpen} />
      
    </div>
  );
};

export default Landing;